import { useEffect } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useBooking } from "@/context/BookingContext";
import { useContent } from "@/context/ContentContext";

// Shared local imagery (auto-optimized WebP on object storage)
export const ROYAL_IMG = {
  clinic: "/api/files/royal-skin-clinic/uploads/811c49a3-1169-4de0-8940-4d3b6c2c7f6b.webp",
  botanical: "/api/files/royal-skin-clinic/uploads/ac61203f-bae3-42c9-b448-bc913f35a778.webp",
  portrait: "/api/files/royal-skin-clinic/uploads/3e7a8338-39cc-486b-9b49-9ebf1c838fb7.webp",
};

// Full-bleed liquid background — slow ken-burns, damped mouse parallax,
// drifting sheen and ivory readability veils. Fills its (relative) parent.
export const RoyalLiquidBg = ({ src, blur = 0, testid, scrim = false }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 42, damping: 18, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 42, damping: 18, mass: 0.6 });

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 18);
      my.set((e.clientY / window.innerHeight - 0.5) * 12);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <>
      {src && (
        <motion.div className="absolute inset-0" style={{ x: sx, y: sy, scale: 1.06 }}>
          <motion.img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            style={{ objectPosition: "72% 28%", filter: blur ? `blur(${blur}px)` : undefined }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, scale: [1, 1.045] }}
            transition={{
              opacity: { duration: 1.2 },
              scale: { duration: 26, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" },
            }}
            data-testid={testid}
          />
        </motion.div>
      )}
      <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-[#F1EBE2] via-[#F1EBE2]/85 to-transparent lg:block" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-[#F1EBE2]/80 lg:hidden" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white/90 to-transparent" aria-hidden="true" />
      <div className="royal-sheen pointer-events-none absolute inset-0" aria-hidden="true" />
      {scrim && (
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-28 bg-gradient-to-l from-[#F1EBE2]/90 via-[#F1EBE2]/40 to-transparent lg:block" aria-hidden="true" />
      )}
    </>
  );
};

// Eyebrow + serif headline block used across Royal Signature pages.
export const RoyalSectionHead = ({ eyebrow, title, testid }) => (
  <div>
    <Reveal>
      <p className="eyebrow-royal" data-testid={testid ? `${testid}-eyebrow` : undefined}>
        {eyebrow}
      </p>
    </Reveal>
    <Reveal delay={0.1}>
      <h2 className="mt-3 font-serif text-3xl font-normal leading-tight text-[#0F0F0F] sm:text-4xl lg:text-5xl" data-testid={testid ? `${testid}-title` : undefined}>
        {title}
      </h2>
    </Reveal>
  </div>
);

// Obsidian consultation band — closes every Royal Signature page.
// Reference layout: copy + CTAs left, brand + location right.
export const RoyalCTA = () => {
  const { book } = useBooking();
  const { content } = useContent();
  const s = content?.settings || {};
  const vis = s.section_visibility?.home_cta ?? true;
  if (!vis) return null;
  const wa = (s.whatsapp || "").replace(/[^\d]/g, "");
  return (
    <section className="bg-[#0F0F0F]" data-testid="royal-cta-band">
      <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-10 px-4 py-16 sm:px-8 lg:flex-row lg:items-center lg:px-12 lg:py-20">
        <div>
          <Reveal>
            <p className="eyebrow-royal !text-[#C5A059]">Your skin deserves a personalised approach</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-xl font-serif text-3xl font-normal leading-tight text-white sm:text-4xl">
              Book Your Consultation Today
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-3.5">
              <button onClick={() => book()} data-testid="royal-cta-book-btn" className="btn-royal-gold px-7 py-3.5 text-[10px]">
                Book Consultation <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              {wa && (
                <a
                  href={`https://wa.me/${wa}?text=${encodeURIComponent("Hello Royal Skin Clinic — I would like to book a consultation.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="royal-cta-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-[5px] border border-white/30 px-7 py-3.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                >
                  Chat on WhatsApp
                </a>
              )}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <div className="lg:text-right">
            {s.brand_lockup_url ? (
              <img src={s.brand_lockup_url} alt="Royal Skin Clinic" className="h-20 w-auto object-contain lg:ml-auto" />
            ) : (
              <p className="leading-none">
                <span className="block font-serif text-xl font-medium uppercase tracking-[0.28em] text-white">Royal</span>
                <span className="mt-1.5 block text-[8px] font-semibold uppercase tracking-[0.42em] text-[#8A8580]">Skin Clinic</span>
              </p>
            )}
            <p className="mt-4 flex items-center gap-1.5 text-[11px] font-light uppercase tracking-[0.18em] text-[#8A8580] lg:justify-end">
              <MapPin className="h-3.5 w-3.5 text-[#C5A059]" aria-hidden="true" /> Dubai, UAE
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
